import { createAutoFormKey, normalizeFormActionUrl } from '@vlt/shared';
import { addCanonicalFormFields } from './formFieldMapping';
import type {
  BootConfig,
  CommonPayload,
  FormTrackingContext,
  LeadPayload,
  Logger,
  Transport,
  VideoSummary,
} from './types';

export const FORM_SELECTOR = 'form';
const TITLE_MAX_LENGTH = 255;
const FIELD_NAME_MAX_LENGTH = 64;
const FIELD_NAME_LIMIT = 50;

export const HIDDEN_FIELDS = [
  'vlt_visitor_id',
  'vlt_session_id',
  'vlt_cid',
  'video_started',
  'video_max_percent',
  'video_completed',
  'video_last_position',
  'video_duration',
  'video_cta_clicked',
  'video_cta_id',
  'video_cta_name',
  'video_cta_attribution',
  'video_id',
  'video_title',
  'video_url',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
  'yclid',
  'page_location',
  'referrer',
] as const;

const HIDDEN_FIELD_SET = new Set<string>(HIDDEN_FIELDS);

type FormTrackerOptions = {
  config: BootConfig;
  getCommonPayload: () => CommonPayload;
  getPrimaryVideoSummary: () => VideoSummary | null;
  transport: Transport;
  logger: Logger;
  datalayer: {
    pushDataLayer: (payload: Record<string, unknown>) => void;
  };
};

function toHiddenValues(
  summary: VideoSummary | null,
  common: CommonPayload,
): Record<string, string> {
  return {
    vlt_visitor_id: common.visitor_id,
    vlt_session_id: common.session_id,
    vlt_cid: common.cid ?? '',
    video_started: String(summary?.video_started ?? false),
    video_max_percent: String(summary?.video_max_percent ?? 0),
    video_completed: String(summary?.video_completed ?? false),
    video_last_position: String(summary?.video_last_position ?? ''),
    video_duration: String(summary?.video_duration ?? ''),
    video_cta_clicked: String(summary?.video_cta_clicked ?? false),
    video_cta_id: summary?.video_cta_id ?? '',
    video_cta_name: summary?.video_cta_name ?? '',
    video_cta_attribution: summary?.video_cta_attribution ?? 'unknown',
    video_id: summary?.video_id ?? '',
    video_title: summary?.video_title ?? '',
    video_url: summary?.video_url ?? '',
    utm_source: common.attribution.utm_source ?? '',
    utm_medium: common.attribution.utm_medium ?? '',
    utm_campaign: common.attribution.utm_campaign ?? '',
    utm_content: common.attribution.utm_content ?? '',
    utm_term: common.attribution.utm_term ?? '',
    gclid: common.attribution.gclid ?? '',
    fbclid: common.attribution.fbclid ?? '',
    yclid: common.attribution.yclid ?? '',
    page_location: common.page_location,
    referrer: common.referrer ?? '',
  };
}

export function injectHiddenFields(
  form: HTMLFormElement,
  summary: VideoSummary | null,
  common: CommonPayload,
): void {
  const values = toHiddenValues(summary, common);

  for (const name of HIDDEN_FIELDS) {
    let input = form.querySelector<HTMLInputElement>(`input[name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = values[name] ?? '';
  }
}

function buildFormData(form: HTMLFormElement): Record<string, unknown> {
  const data: Record<string, unknown> = {};
  for (const [key, value] of new FormData(form).entries()) {
    data[key] = typeof value === 'string' ? value : value.name;
  }
  addCanonicalFormFields(form, data);
  return data;
}

function buildLeadPayload(
  form: HTMLFormElement,
  config: BootConfig,
  summary: VideoSummary | null,
  common: CommonPayload,
  context: FormTrackingContext,
): LeadPayload {
  const payload: LeadPayload = {
    ...common,
    form_id: context.formId,
    form_data: buildFormData(form),
    video_summary: summary,
    submitted_at: new Date().toISOString(),
  };

  if (context.autoDetected) {
    payload.source_url = context.sourceUrl;
    payload.detected_title = context.detectedTitle;
    payload.page_heading = context.pageHeading;
    payload.field_names = context.fieldNames;
  }

  return payload;
}

function dataLayerLeadPayload(
  summary: VideoSummary | null,
  common: CommonPayload,
): Record<string, unknown> {
  return {
    event: 'lead_form_submit',
    vlt_visitor_id: common.visitor_id,
    vlt_session_id: common.session_id,
    vlt_cid: common.cid,
    ...common.attribution,
    page_location: common.page_location,
    referrer: common.referrer,
    video_id: summary?.video_id,
    video_title: summary?.video_title,
    video_url: summary?.video_url,
    video_max_percent: summary?.video_max_percent,
    video_last_position: summary?.video_last_position,
    video_duration: summary?.video_duration,
    video_completed: summary?.video_completed,
    video_cta_clicked: summary?.video_cta_clicked,
    video_cta_id: summary?.video_cta_id,
    video_cta_name: summary?.video_cta_name,
    video_cta_attribution: summary?.video_cta_attribution,
  };
}

function truncate(value: string, maxLength: number): string {
  return value.length > maxLength ? value.slice(0, maxLength) : value;
}

function normalizeText(value: string | null | undefined, maxLength: number): string | null {
  const normalized = value?.trim().replace(/\s+/g, ' ');
  if (!normalized) return null;
  return truncate(normalized, maxLength);
}

function getDetectedTitle(form: HTMLFormElement): string | null {
  return (
    normalizeText(form.getAttribute('aria-label'), TITLE_MAX_LENGTH) ??
    normalizeText(form.querySelector('legend')?.textContent, TITLE_MAX_LENGTH)
  );
}

function getPageHeading(form: HTMLFormElement): string | null {
  const section = form.closest('section, article, main, header, aside');
  const sectionHeading = normalizeText(
    section?.querySelector<HTMLElement>('h1, h2, h3')?.textContent,
    TITLE_MAX_LENGTH,
  );
  if (sectionHeading) return sectionHeading;

  let sibling = form.previousElementSibling;
  while (sibling) {
    if (sibling.matches('h1, h2, h3')) {
      return normalizeText(sibling.textContent, TITLE_MAX_LENGTH);
    }
    const nested = sibling.querySelector<HTMLElement>('h1, h2, h3');
    const nestedHeading = normalizeText(nested?.textContent, TITLE_MAX_LENGTH);
    if (nestedHeading) return nestedHeading;
    sibling = sibling.previousElementSibling;
  }

  return null;
}

function isVisibleField(
  field: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement,
): boolean {
  if (field instanceof HTMLInputElement && field.type.toLowerCase() === 'hidden') return false;
  if (field.hidden || field.closest('[hidden], [aria-hidden="true"]')) return false;

  const getComputedStyleFn = (
    globalThis as { getComputedStyle?: (element: Element) => CSSStyleDeclaration }
  ).getComputedStyle;
  if (!getComputedStyleFn) return true;

  const style = getComputedStyleFn(field);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

function collectFieldNames(form: HTMLFormElement): string[] {
  const names: string[] = [];
  const seen = new Set<string>();

  for (const field of form.querySelectorAll<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >('input[name], select[name], textarea[name]')) {
    if (!isVisibleField(field)) continue;

    const name = normalizeText(field.getAttribute('name'), FIELD_NAME_MAX_LENGTH);
    if (!name || HIDDEN_FIELD_SET.has(name) || seen.has(name)) continue;

    seen.add(name);
    names.push(name);
    if (names.length >= FIELD_NAME_LIMIT) break;
  }

  return names;
}

async function createAutoTrackingContext(
  form: HTMLFormElement,
): Promise<FormTrackingContext | null> {
  const rawAction = form.getAttribute('action');
  const keyMaterial = rawAction?.trim() ? rawAction : window.location.href;
  const sourceUrl = normalizeFormActionUrl(keyMaterial, { pageHref: window.location.href });
  if (!sourceUrl) return null;

  const fieldNames = collectFieldNames(form);
  const formId = await createAutoFormKey(keyMaterial, { pageHref: window.location.href });

  if (!formId) return null;

  return {
    formId,
    autoDetected: true,
    sourceUrl,
    detectedTitle: getDetectedTitle(form),
    pageHeading: getPageHeading(form),
    fieldNames,
  };
}

function createLegacyTrackingContext(form: HTMLFormElement): FormTrackingContext | null {
  const formId = form.dataset.vltFormId;
  if (!formId) return null;

  return {
    formId,
    autoDetected: false,
    sourceUrl: null,
    detectedTitle: null,
    pageHeading: null,
    fieldNames: null,
  };
}

export function createFormTracker({
  config,
  getCommonPayload,
  getPrimaryVideoSummary,
  transport,
  logger,
  datalayer,
}: FormTrackerOptions) {
  const attached = new WeakSet<HTMLFormElement>();

  function attachWithContext(
    form: HTMLFormElement,
    contextOrPromise: FormTrackingContext | Promise<FormTrackingContext | null>,
  ): void {
    form.addEventListener(
      'submit',
      (ev) => {
        const submitWithContext = (
          context: FormTrackingContext | null,
          submitAfter = false,
        ): void => {
          if (!context) {
            logger.debug('form.auto_detect_skipped', { reason: 'missing_key_material' });
            if (submitAfter) form.submit();
            return;
          }

          const summary = getPrimaryVideoSummary();
          const common = getCommonPayload();
          injectHiddenFields(form, summary, common);
          const payload = buildLeadPayload(form, config, summary, common, context);

          logger.debug('form.submit', {
            formId: context.formId,
            autoDetected: context.autoDetected,
          });

          if (config.formSubmitMode === 'non_blocking') {
            transport.sendLead(payload);
            datalayer.pushDataLayer(dataLayerLeadPayload(summary, common));
            return;
          }

          if (config.formSubmitMode === 'blocking') {
            transport.sendLeadAwait(payload).finally(() => form.submit());
            return;
          }

          if (config.formSubmitMode === 'api_only') {
            transport
              .sendLeadAwait(payload)
              .catch((error) => logger.warn('form.api_only_failed', error));
          }
        };
        const isPendingContext =
          typeof (contextOrPromise as Promise<FormTrackingContext | null>).then === 'function';

        if (config.formSubmitMode === 'blocking') {
          ev.preventDefault();
          if (isPendingContext) {
            (contextOrPromise as Promise<FormTrackingContext | null>)
              .then((context) => submitWithContext(context, true))
              .catch((error) => {
                logger.warn('form.auto_detect_failed', { error });
                form.submit();
              });
            return;
          }

          submitWithContext(contextOrPromise as FormTrackingContext);
          return;
        }

        if (isPendingContext) {
          (contextOrPromise as Promise<FormTrackingContext | null>)
            .then((context) => submitWithContext(context))
            .catch((error) => logger.warn('form.auto_detect_failed', { error }));
          return;
        }

        submitWithContext(contextOrPromise as FormTrackingContext);
      },
      { capture: true },
    );
  }

  function attach(form: HTMLFormElement): void {
    if (attached.has(form)) return;
    attached.add(form);

    const legacyContext = createLegacyTrackingContext(form);
    if (legacyContext) {
      attachWithContext(form, legacyContext);
      return;
    }

    attachWithContext(form, createAutoTrackingContext(form));
  }

  function observe(): MutationObserver | null {
    if (!('MutationObserver' in window)) return null;
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          if (node.matches(FORM_SELECTOR)) attach(node as HTMLFormElement);
          node.querySelectorAll<HTMLFormElement>(FORM_SELECTOR).forEach(attach);
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    return observer;
  }

  return { attach, observe };
}
